import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from '@angular/router';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { LoginService } from '../../pages/login/login.service';
import { AjaxLoaderService } from '../ajaxLoader.service';
import { AuthService } from './auth.service';
import { Observable, BehaviorSubject, throwError } from 'rxjs';

@Injectable()
export class JwtHttpInterceptor implements HttpInterceptor
{
	private refreshTokenInProgress = false;

	// Refresh Token Subject tracks the current token, or is null if no token is currently
	// available (e.g. refresh pending).
	private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

	// constructor
	constructor
		(
			private router: Router,
			private ajaxLoader: AjaxLoaderService,
			public loginService: LoginService
		)
	{
	}

	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>>
	{
		// ajax loader
		//this.ajaxLoader.show();

		// access token
		const accessToken = AuthService.getAccessToken();

		if (accessToken)
		{
			request = request.clone({
				setHeaders: {
					Authorization: `Bearer ${accessToken}`
				}
			});
		}

		return next.handle(request)
			.pipe(tap(
				(event: HttpEvent<any>) =>
				{
					if (event instanceof HttpResponse)
					{
						// ajax loader
						//this.ajaxLoader.hide();
					}
				}),
				catchError(error =>
				{
					// Unauthorized
					if (error.status === 401 || error.status === 403)
					{
						// check for token expired header
						if (this.tokenExpired(accessToken))
						{
							if (this.refreshTokenInProgress)
							{
								// If refreshTokenInProgress is true, we will wait until refreshTokenSubject has a non-null value
								// – which means the new token is ready and we can retry the request again
								return this.refreshTokenSubject
									.pipe(
										filter(result => result !== null),
										take(1),
										switchMap(() => next.handle(this.addAuthenticationToken(request)))
									)
							}
							else
							{
								this.refreshTokenInProgress = true;

								// Set the refreshTokenSubject to null so that subsequent API calls will wait until the new token has been retrieved
								this.refreshTokenSubject.next(null);

								// check: access token and refresh token both present
								const token = AuthService.getAccessToken();
								const refreshToken = AuthService.getRefreshToken();

								if (token && refreshToken)
								{
									return this.loginService
										.refreshToken({ token: token, refreshToken: refreshToken })
										.pipe(
											switchMap(result =>
											{
												//saving new tokens
												AuthService.setAccessToken(result.token);
												AuthService.setRefreshToken(result.refreshToken);

												//When the call to refreshToken completes we reset the refreshTokenInProgress to false
												// for the next time the token needs to be refreshed
												this.refreshTokenInProgress = false;
												this.refreshTokenSubject.next(result.token);

												// re-executing request with new token
												return next.handle(this.addAuthenticationToken(request)).pipe(tap(
													(event: HttpEvent<any>) =>
													{
														if (event instanceof HttpResponse)
														{
															// ajax loader
															//this.ajaxLoader.hide();
														}
													})
												);
											}),
											catchError(() =>
											{
												this.refreshTokenInProgress = false;

												// ajax loader
												//this.ajaxLoader.hide();

												this.loginService.logout();
												return throwError(error);
											})
										)
								}

								// no access/refresh token
								else
								{
									this.loginService.logout();
								}
							}
						}
						else
						{
							// ajax loader
							//this.ajaxLoader.hide();

							// forbidden
							if (error.status === 401)
								this.router.navigate(['login'], { queryParams: { returnUrl: this.router.url } });

							else if (error.status === 403)
								this.router.navigate(['forbidden']);
						}
					}

					// generic error
					else
					{
						// continuing request execution
						return throwError(error);
					}
				})
			);
	}

	// check: expired token
	tokenExpired(token: string)
	{
		const expiry = (JSON.parse(atob(token.split('.')[1]))).exp;
		return (Math.floor((new Date).getTime() / 1000)) >= expiry;
	}

	// add authentication token
	addAuthenticationToken(request)
	{
		// Get access token from Local Storage
		const accessToken = AuthService.getAccessToken();

		// If access token is null this means that user is not logged in
		// And we return the original request
		if (!accessToken)
			return request;

		// We clone the request, because the original request is immutable
		return request.clone({
			setHeaders: {
				Authorization: `Bearer ${AuthService.getAccessToken()}`
			}
		});
	}
}